import React, { useState, useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { RuleTemplateListItem } from '../../../types/frontend';

interface TemplateOption {
  id: string;
  label: string;
  group: string;
  item: RuleTemplateListItem;
}

function buildOptions(templates: RuleTemplateListItem[]): TemplateOption[] {
  return templates.map((t) => ({
    id: t.template_id,
    label: t.name,
    group: t.category || 'Other',
    item: t,
  }));
}

export interface TemplatePickerProps {
  templates: RuleTemplateListItem[];
  value: string | null;
  onChange: (templateId: string | null) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

const TemplatePicker: React.FC<TemplatePickerProps> = ({
  templates,
  value,
  onChange,
  error,
  helperText,
  required,
  disabled,
  loading,
}) => {
  const [inputValue, setInputValue] = useState('');

  const options = useMemo(() => buildOptions(templates), [templates]);
  const selected = options.find((o) => o.id === value) ?? null;

  return (
    <Autocomplete
      options={options}
      groupBy={(o) => o.group}
      getOptionLabel={(o) => o.label}
      value={selected}
      onChange={(_, v) => onChange(v ? v.id : null)}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(opts) => {
        if (!inputValue.trim()) return opts;
        const q = inputValue.toLowerCase();
        return opts.filter(
          (o) =>
            o.label.toLowerCase().includes(q) ||
            o.item.description.toLowerCase().includes(q) ||
            o.group.toLowerCase().includes(q),
        );
      }}
      loading={loading}
      disabled={disabled || loading}
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          label="Rule template"
          required={required}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
          placeholder="Search templates…"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2">{option.label}</Typography>
            {option.item.description && (
              <Typography variant="caption" color="text.secondary">
                {option.item.description}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderGroup={(params) => (
        <Box key={params.key}>
          <Box sx={{ px: 1.5, pt: 1, pb: 0.25 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="primary.main"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              {params.group}
            </Typography>
          </Box>
          {params.children}
        </Box>
      )}
      noOptionsText={loading ? 'Loading templates…' : 'No templates found'}
    />
  );
};

export default TemplatePicker;
